import axios from 'axios';
import { auth } from './firebase';
import type {
  OskiChatRequest,
  OskiChatResponse,
  Ticket,
  TeamUser,
  AgentStatus,
  ApiResponse,
  AppConfig,
  AuditLogResponse,
} from '../types';

const BASE_URL = import.meta.env.VITE_BACKEND_URL || 'https://team-backend-398272991022.us-central1.run.app';

// ─── Axios instance ────────────────────────────────────────────────────────
const api = axios.create({ baseURL: BASE_URL });

// Attach Firebase ID token to every request
api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Oski chat (SSE) ─────────────────────────────────────────────────────
export async function chatWithOski(
  payload: OskiChatRequest,
  onStatus?: (text: string) => void
): Promise<OskiChatResponse> {
  const user  = auth.currentUser;
  const token = user ? await user.getIdToken() : '';

  const res = await fetch(`${BASE_URL}/team/oski`, {
    method:  'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok || !res.body) throw new Error('Request failed');

  const reader  = res.body.getReader();
  const decoder = new TextDecoder();
  let   buffer  = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const parts = buffer.split('\n\n');
    buffer = parts.pop() ?? '';

    for (const part of parts) {
      const eventLine = part.match(/^event: (.+)$/m)?.[1];
      const dataLine  = part.match(/^data: (.+)$/m)?.[1];
      if (!dataLine) continue;

      const parsed = JSON.parse(dataLine);
      if (eventLine === 'status') { onStatus?.(parsed.text); continue; }
      if (eventLine === 'done')   return parsed as OskiChatResponse;
      if (eventLine === 'error')  throw new Error(parsed.error);
    }
  }

  throw new Error('Stream ended without response');
}

// ─── Tickets ───────────────────────────────────────────────────────────────
export async function getTickets(): Promise<Ticket[]> {
  const { data } = await api.get<Ticket[]>('/team/tickets');
  return data;
}

export async function createTicket(
  ticket: Partial<Ticket>
): Promise<Ticket> {
  const { data } = await api.post<Ticket>('/team/tickets', ticket);
  return data;
}

export async function updateTicketStatus(
  notionId: string,
  status: Ticket['status']
): Promise<void> {
  await api.patch(`/team/tickets/${notionId}/status`, { status });
}

export async function updateTicketContext(
  notionId: string,
  technicalContext: string
): Promise<void> {
  await api.patch(`/team/tickets/${notionId}/context`, { technicalContext });
}

// ─── Users ─────────────────────────────────────────────────────────────────
export async function getTeamUsers(): Promise<TeamUser[]> {
  const { data } = await api.get<TeamUser[]>('/team/users');
  return data;
}

export async function updateUser(
  uid: string,
  patch: {
    trustLevel?:    TeamUser['trustLevel'];
    isSysadmin?:    boolean;
    appRoles?:      TeamUser['appRoles'];
    permissions?:   TeamUser['permissions'];
    validatorUid?:  string;
    validatorName?: string;
  }
): Promise<void> {
  await api.patch(`/team/users/${uid}`, patch);
}

export async function getValidators(): Promise<
  Pick<TeamUser, 'uid' | 'displayName' | 'trustLevel'>[]
> {
  const { data } = await api.get('/team/users/validators');
  return data;
}

export async function getOrCreateUser(user: {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
}): Promise<TeamUser> {
  const { data } = await api.post<TeamUser>('/team/users/sync', user);
  return data;
}

// ─── Repo status ──────────────────────────────────────────────────────────

export interface RepoStatus {
  repo:          string;
  indexed:       boolean;
  treeSize:      number;
  lastIndexedAt: string | null;
  lastSha:       string | null;
  pame: { access: string; permission: string };
  neo:  { access: string; permission: string };
}

export async function getReposStatus(repos: string[]): Promise<RepoStatus[]> {
  const { data } = await api.get<RepoStatus[]>('/team/repos/status', {
    params: { repos: repos.join(',') },
  });
  return data;
}

export async function indexRepo(repo: string): Promise<{ ok: boolean; treeSize: number }> {
  const { data } = await api.post('/team/repos/index', { repo });
  return data;
}

// ─── App configs (colores) ────────────────────────────────────────────────
export async function getAppConfigs(): Promise<{ apps: AppConfig[]; notionDbUrl: string } | AppConfig[]> {
  const { data } = await api.get<{ apps: AppConfig[]; notionDbUrl: string } | AppConfig[]>('/team/apps');
  return data;
}

export async function updateApp(appId: string, fields: { color?: string; label?: string; prefix?: string; status?: 'enabled' | 'suspended' | 'disabled' }): Promise<void> {
  await api.put(`/team/apps/${appId}`, fields);
}

// ─── Agent config (sysadmin) ──────────────────────────────────────────────
export interface AgentConfig {
  name:         string;
  role:         string;
  language:     string;
  model:        string;
  planModel?:   string;
  maxTokens:    number;
  temperature:  number;
  systemPrompt: string;
  memory:       string;
  repos?:       string[];
  testRepos?:   string[];
}

export async function getAgentsConfig(): Promise<{ config: Record<string, AgentConfig>; defaults: Record<string, AgentConfig> }> {
  const { data } = await api.get('/team/config/agents');
  return data;
}

export async function saveAgentConfig(agentId: string, patch: Partial<AgentConfig>): Promise<void> {
  await api.put(`/team/config/agents/${agentId}`, patch);
}

// ─── Rechazar ticket ───────────────────────────────────────────────────────
export async function rejectTicket(notionId: string, closePr: boolean, prUrl?: string): Promise<{ ok: boolean; prClosed: boolean }> {
  const user  = auth.currentUser;
  const token = user ? await user.getIdToken() : '';

  const res = await fetch(`${BASE_URL}/team/tickets/${notionId}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ closePr, prUrl }),
  });

  if (!res.ok) throw new Error('No se pudo rechazar el ticket');
  return res.json();
}

// ─── Ticket: aprobar y enviar a Pame (SR/DEV/SYS) ────────────────────────────
export async function sendToPame(notionId: string): Promise<void> {
  const user  = auth.currentUser;
  const token = user ? await user.getIdToken() : '';
  const res = await fetch(`${BASE_URL}/team/tickets/${notionId}/send-to-pame`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? 'No se pudo enviar a Pame');
  }
}

// ─── Neo: listar runs archivados ───────────────────────────────────────────
export type ArchivedNeoRun = {
  archivedAt: string;
  state:      'idle' | 'running' | 'done' | 'error';
  log:        string[];
  prUrl:      string;
  branchName: string;
  error:      string;
  startedAt:  string | null;
  finishedAt: string | null;
};

export async function listNeoRuns(notionId: string): Promise<ArchivedNeoRun[]> {
  const user  = auth.currentUser;
  const token = user ? await user.getIdToken() : '';

  const res = await fetch(`${BASE_URL}/team/tickets/${notionId}/neo/runs`, {
    method:  'GET',
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!res.ok) throw new Error('No se pudieron cargar los runs anteriores');
  const body = await res.json() as { runs?: ArchivedNeoRun[] };
  return body.runs ?? [];
}

// ─── Neo: reset job pegado ─────────────────────────────────────────────────
export async function resetNeo(notionId: string): Promise<void> {
  const user  = auth.currentUser;
  const token = user ? await user.getIdToken() : '';

  const res = await fetch(`${BASE_URL}/team/tickets/${notionId}/neo/reset`, {
    method: 'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!res.ok) throw new Error('No se pudo resetear Neo');
}

// ─── Neo: implementar ticket (SSE) ───────────────────────────────────────────
// onLog: callback que recibe cada línea de status en tiempo real vía SSE.
// Firestore onSnapshot sigue siendo la fuente de verdad para persistencia y reconexión.
export async function runNeo(notionId: string, onLog?: (text: string) => void): Promise<void> {
  return streamNeoSSE(`${BASE_URL}/team/tickets/${notionId}/neo`, onLog);
}

export async function resumeNeo(notionId: string, onLog?: (text: string) => void): Promise<void> {
  return streamNeoSSE(`${BASE_URL}/team/tickets/${notionId}/neo/resume`, onLog);
}

// Responde la pregunta de rescue de Pame y reanuda Neo desde
// awaiting_clarification. La respuesta queda en ticket_chats.thread y se
// inyecta en technicalContext del próximo run.
export async function answerNeoRescue(
  notionId: string,
  answer: string,
  onLog?: (text: string) => void,
): Promise<void> {
  return streamNeoSSE(
    `${BASE_URL}/team/tickets/${notionId}/neo/answer-rescue`,
    onLog,
    { answer },
  );
}

async function streamNeoSSE(url: string, onLog?: (text: string) => void, body?: unknown): Promise<void> {
  const user  = auth.currentUser;
  const token = user ? await user.getIdToken() : '';

  const res = await fetch(url, {
    method:  'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? 'No se pudo iniciar Neo');
  }

  if (!res.body) return;

  const reader  = res.body.getReader();
  const decoder = new TextDecoder();
  let   buffer  = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const parts = buffer.split('\n\n');
      buffer = parts.pop() ?? '';

      for (const part of parts) {
        const eventLine = part.match(/^event: (.+)$/m)?.[1];
        const dataLine  = part.match(/^data: (.+)$/m)?.[1];
        if (!dataLine) continue;

        const parsed = JSON.parse(dataLine) as { error?: string; text?: string };
        if (eventLine === 'error') throw new Error(parsed.error ?? 'Error en Neo');
        if (eventLine === 'status' && parsed.text && onLog) onLog(parsed.text);
      }
    }
  } catch (err) {
    // Si el stream se corta por desconexión del cliente, no es un error real
    if (err instanceof Error && err.message !== 'Error en Neo' && !err.message.includes('No se pudo')) return;
    throw err;
  }
}

// ─── Agent health check ────────────────────────────────────────────────────
export async function checkAgentHealth(): Promise<AgentStatus> {
  const start = Date.now();
  try {
    const { data } = await api.get('/team/health', { timeout: 10000 });
    const ms = Date.now() - start;
    return {
      name:      'oski',
      status:    data.status === 'ok' ? (ms > 5000 ? 'degraded' : 'ok') : 'error',
      latencyMs: ms,
      checkedAt: new Date().toISOString(),
      services:  data.services,
    };
  } catch {
    return {
      name:      'oski',
      status:    'error',
      checkedAt: new Date().toISOString(),
    };
  }
}

// ─── Audit log (sysadmin) ───────────────────────────────────────────────────
export async function getAuditPrLog(params: { limit?: number; since?: string } = {}): Promise<AuditLogResponse> {
  const query: Record<string, string | number> = {};
  if (params.limit !== undefined) query.limit = params.limit;
  if (params.since) query.since = params.since;
  const { data } = await api.get<AuditLogResponse>('/team/audit/pr-log', { params: query });
  return data;
}
